import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core"
import { SortableContext, rectSortingStrategy, useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Card, Col, Flex, Result, Row, message } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { neutral, shadows } from "./dashboardTokens"

import { WidgetRenderer } from "../../../../components/widgets/WidgetRenderer"
import { getUIPreferences } from "../../../User/services"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch"
import { useAppSelector } from "../../../../hooks/use-app-selector"
import { useDebouncedCallback } from "../../../../hooks/use-debounced-callback"
import { useGlobalFilterOptions } from "../../hooks/useGlobalFilterOptions"
import {
  createEmptyFilters,
  hasActiveFilters,
  parseActiveViewGlobalFilters,
  parseGlobalFilters
} from "../../redux/globalFilters"
import { fetchNba, fetchPortfolio, setGlobalFilters } from "../../redux/portfolio.slice"
import type {
  DashboardCatalogEntry,
  DashboardTile,
  FilterLabels,
  GlobalFilters
} from "../../redux/portfolio.types"
import { AddTileDrawer } from "./AddTileDrawer"
import { ExportButtons } from "./ExportButtons"
import { FilterBar } from "./FilterBar"
import { KPI_CARD_COL } from "./kpiGrid"
import {
  cashFlowWidget,
  feedWidget,
  kpiWidget,
  placeholderWidget,
  projectStatusWidget,
} from "./portfolioWidgetAdapter"
import { SavedViewBar } from "./SavedViewBar"
import { TimeWindowControl } from "./TimeWindowControl"
import { useDashboardLayout } from "./useDashboardLayout"

const NBA_POLL_INTERVAL = 5 * 60 * 1000

interface SortableKPICardProps {
  tile: DashboardTile
  onHide?: (id: string) => void
  currency?: string
}

const SortableKPICard = ({ tile, onHide, currency }: SortableKPICardProps) => {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: tile.id })
  const style = { transform: CSS.Transform.toString(transform), transition }
  return (
    <Col {...KPI_CARD_COL} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div style={{ position: "relative" }}>
        {onHide && (
          <Button
            type="text"
            size="small"
            aria-label={t("portfolio.editLayout.hide", "Hide")}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onHide(tile.id)}
            style={{ position: "absolute", top: 4, right: 4, zIndex: 1, lineHeight: 1 }}
          >
            ×
          </Button>
        )}
        <WidgetRenderer {...kpiWidget(tile, currency)} state="ready" />
      </div>
    </Col>
  )
}

interface PlaceholderCardProps {
  entry: DashboardCatalogEntry
  onHide?: (id: string) => void
}

const PlaceholderCard = ({ entry, onHide }: PlaceholderCardProps) => {
  const { t } = useTranslation()
  return (
    <Col {...KPI_CARD_COL}>
      <div style={{ position: "relative" }}>
        {onHide && (
          <Button
            type="text"
            size="small"
            aria-label={t("portfolio.editLayout.hide", "Hide")}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onHide(entry.id)}
            style={{ position: "absolute", top: 4, right: 4, zIndex: 1, lineHeight: 1 }}
          >
            ×
          </Button>
        )}
        <WidgetRenderer {...placeholderWidget(entry)} state="ready" />
      </div>
    </Col>
  )
}

const StaticKPICard = ({ tile, currency }: { tile: DashboardTile; currency?: string }) => (
  <Col {...KPI_CARD_COL}>
    <WidgetRenderer {...kpiWidget(tile, currency)} state="ready" />
  </Col>
)

export const PortfolioDashboard = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { status, bundle, nba, window, globalFilters } = useAppSelector((s) => s.portfolio)
  const didInit = useRef(false)
  const [addTileOpen, setAddTileOpen] = useState(false)
  const windowRef = useRef(window)
  windowRef.current = window
  const [selectedWindowPreset, setSelectedWindowPreset] = useState(window.preset)

  useEffect(() => {
    setSelectedWindowPreset(window.preset)
  }, [window.preset])

  const layout = useDashboardLayout()
  const { options: filterOptions, loading: filterOptionsLoading } =
    useGlobalFilterOptions()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  useEffect(() => {
    if (!layout.isEditing) setAddTileOpen(false)
  }, [layout.isEditing])

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true
      void dispatch(fetchPortfolio(windowRef.current))
      void dispatch(fetchNba())
      void (async () => {
        try {
          const prefs = await getUIPreferences()
          if (prefs.ok && prefs.data) {
            layout.initFromConfig(prefs.data.dashboard_config)
            const restored = parseActiveViewGlobalFilters(prefs.data.dashboard_config)
            dispatch(setGlobalFilters(restored))
            if (hasActiveFilters(restored)) {
              void dispatch(fetchPortfolio(windowRef.current))
            }
          }
        } catch {
          // best-effort: fall back to the default layout / unfiltered
        }
      })()
    }

    const intervalId = setInterval(() => {
      void dispatch(fetchNba())
    }, NBA_POLL_INTERVAL)

    return () => clearInterval(intervalId)
  }, [dispatch])

  const windowKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const key = JSON.stringify(window)
    if (windowKeyRef.current === undefined) {
      windowKeyRef.current = key
      return
    }
    if (windowKeyRef.current !== key) {
      windowKeyRef.current = key
      void dispatch(fetchPortfolio(window))
    }
  }, [window, dispatch])

  // Collapse a burst of multi-select tag edits into one refetch + one persist so
  // rapid changes don't fire N portfolio aggregations or race N ui-preferences PATCHes.
  const applyFiltersDebounced = useDebouncedCallback(
    (next: GlobalFilters, nextLabels: FilterLabels, viewId: string | null) => {
      void dispatch(fetchPortfolio(windowRef.current))
      void layout.persistGlobalFilters(next, nextLabels, viewId).catch(() => {
        void message.error(t("portfolio.filters.persistFailed", "Failed to save filters"))
      })
    },
    350
  )

  if (status === "unavailable") return null

  if (status === "loading" || status === "idle") {
    return (
      <Flex vertical gap="middle">
        <Row gutter={[16, 16]}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Col key={i} {...KPI_CARD_COL}>
              <WidgetRenderer widget={{ id: `sk${i}`, type: "kpi" }} payload={null} state="loading" />
            </Col>
          ))}
        </Row>
      </Flex>
    )
  }

  if (status === "error") {
    return (
      <Result
        status="error"
        title={t("portfolio.error.title")}
        extra={
          <Button onClick={() => void dispatch(fetchPortfolio(window))}>
            {t("portfolio.error.retry")}
          </Button>
        }
      />
    )
  }

  const allValues = bundle?.all_values
  const hasAllValues = Boolean(allValues)

  const resolveTile = (id: string): DashboardTile | null => {
    if (!bundle) return null
    const tile = bundle.tiles.find((t) => t.id === id)
    if (tile) return tile
    return allValues?.[id] ?? null
  }

  const effectiveOrder = layout.order.length
    ? layout.order
    : (bundle?.tiles ?? []).map((t) => t.id)

  const shownIds = effectiveOrder.filter((id) => !layout.hidden.includes(id))

  const shownCount = shownIds.filter((id) => resolveTile(id) !== null).length

  const catalogEntries = bundle
    ? (bundle.catalog ?? []).filter((c) => !shownIds.includes(c.id))
    : []

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      layout.reorder(String(active.id), String(over.id))
    }
  }

  const handleSave = async () => {
    try {
      await layout.save()
      void dispatch(fetchPortfolio(windowRef.current))
    } catch {
      void message.error(t("portfolio.editLayout.saveFailed"))
    }
  }

  const handleFiltersChange = (next: GlobalFilters, nextLabels: FilterLabels) => {
    dispatch(setGlobalFilters(next))
    applyFiltersDebounced(next, nextLabels, layout.activeViewId)
  }

  const handleSwitch = async (id: string) => {
    try {
      const target = layout.views.find((view) => view.id === id)
      const targetFilters = parseGlobalFilters(target)
      const filtersChanged =
        JSON.stringify(targetFilters) !==
        JSON.stringify(globalFilters ?? parseGlobalFilters(null))
      await layout.switchView(id)
      dispatch(setGlobalFilters(targetFilters))
      if (!hasAllValues || filtersChanged) {
        void dispatch(fetchPortfolio(windowRef.current))
      }
    } catch {
      void message.error(t("portfolio.editLayout.switchFailed", "Failed to switch view"))
    }
  }

  const handleCreate = async (name: string, from: "current" | "default") => {
    try {
      const source =
        from === "default"
          ? layout.views.find((view) => view.id === layout.defaultViewId)
          : layout.views.find((view) => view.id === layout.activeViewId)
      await layout.createView(name, from)
      dispatch(setGlobalFilters(parseGlobalFilters(source)))
      void dispatch(fetchPortfolio(windowRef.current))
    } catch {
      void message.error(t("portfolio.editLayout.createFailed", "Failed to create view"))
    }
  }

  const handleRename = async (id: string, name: string) => {
    try {
      await layout.renameView(id, name)
    } catch {
      void message.error(t("portfolio.editLayout.renameFailed", "Failed to rename view"))
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const fallback =
        id === layout.activeViewId
          ? layout.views.find((view) => view.id !== id)
          : undefined
      await layout.deleteView(id)
      if (fallback) {
        dispatch(setGlobalFilters(parseGlobalFilters(fallback)))
        void dispatch(fetchPortfolio(windowRef.current))
      }
    } catch {
      void message.error(t("portfolio.editLayout.deleteFailed", "Failed to delete view"))
    }
  }

  const handleSetDefault = async (id: string) => {
    try {
      await layout.setDefault(id)
    } catch {
      void message.error(t("portfolio.editLayout.setDefaultFailed", "Failed to set default"))
    }
  }

  const handleRestoreDefault = async () => {
    try {
      await layout.restoreDefault()
      dispatch(setGlobalFilters(createEmptyFilters()))
      void dispatch(fetchPortfolio(windowRef.current))
    } catch {
      void message.error(t("portfolio.editLayout.restoreFailed"))
    }
  }

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center" wrap="wrap" gap="small">
        <SavedViewBar
          views={layout.views}
          activeViewId={layout.activeViewId}
          activeView={layout.activeView}
          defaultViewId={layout.defaultViewId}
          isEditing={layout.isEditing}
          startEdit={() => layout.startEdit(effectiveOrder)}
          cancel={layout.cancel}
          save={handleSave}
          restoreDefault={handleRestoreDefault}
          onAddTile={() => setAddTileOpen(true)}
          onSwitch={handleSwitch}
          onCreate={handleCreate}
          onRename={handleRename}
          onDelete={handleDelete}
          onSetDefault={handleSetDefault}
        />
        <ExportButtons window={window} selectedPreset={selectedWindowPreset} />
      </Flex>

      <FilterBar
        filters={globalFilters ?? parseGlobalFilters(null)}
        options={filterOptions}
        filterLabels={layout.filterLabels}
        dateControl={
          <div data-testid="dashboard-date-filter">
            <TimeWindowControl
              selectedPreset={selectedWindowPreset}
              onSelectedPresetChange={setSelectedWindowPreset}
            />
          </div>
        }
        loading={filterOptionsLoading}
        onChange={handleFiltersChange}
      />

      {bundle && (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "4px 2px 10px" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: neutral.ink }}>
              {t("portfolio.section.overview", "Portfolio overview")}
            </span>
            <span style={{ fontSize: 12, color: neutral.faint }}>
              {t("portfolio.section.metrics", "{{count}} metrics", {
                count: shownCount,
              })}
            </span>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              {layout.isEditing ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={shownIds.filter((id) => resolveTile(id) !== null)}
                    strategy={rectSortingStrategy}
                  >
                    <Row gutter={[16, 16]}>
                      {shownIds.map((id) => {
                        const tile = resolveTile(id)
                        if (tile) {
                          return (
                            <SortableKPICard
                              key={id}
                              tile={tile}
                              onHide={layout.toggleHidden}
                              currency={bundle.cash_flow?.currency}
                            />
                          )
                        }
                        const entry = bundle.catalog?.find((c) => c.id === id)
                        return entry ? (
                          <PlaceholderCard key={id} entry={entry} onHide={layout.toggleHidden} />
                        ) : null
                      })}
                    </Row>
                  </SortableContext>
                </DndContext>
              ) : (
                <Row gutter={[16, 16]}>
                  {shownIds.map((id) => {
                    const tile = resolveTile(id)
                    return tile ? (
                      <StaticKPICard key={id} tile={tile} currency={bundle.cash_flow?.currency} />
                    ) : null
                  })}
                </Row>
              )}
            </Col>
            <Col xs={24} lg={8}>
              <Card
                title={t("portfolio.cashFlow.title")}
                size="small"
                style={{
                  border: `1px solid ${neutral.border}`,
                  borderRadius: 12,
                  boxShadow: shadows.card,
                }}
              >
                <WidgetRenderer {...cashFlowWidget(bundle.cash_flow)} state="ready" />
              </Card>
            </Col>
          </Row>

          <AddTileDrawer
            open={layout.isEditing && addTileOpen}
            catalogTiles={catalogEntries}
            onAdd={layout.addTile}
            onClose={() => setAddTileOpen(false)}
          />

          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card
                title={t("portfolio.status.title")}
                size="small"
                style={{
                  border: `1px solid ${neutral.border}`,
                  borderRadius: 12,
                  boxShadow: shadows.card,
                }}
              >
                <WidgetRenderer {...projectStatusWidget(bundle.project_status)} state="ready" />
              </Card>
            </Col>
          </Row>
        </>
      )}

      <WidgetRenderer {...feedWidget(nba)} state="ready" />
    </Flex>
  )
}
